import { type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { useAuth } from "@/auth/AuthProvider";
import type { CapabilityNeed } from "@/auth/capabilities";
import { usePlatformCan, type PlatformNeed } from "@/platform/access";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "teal" | "platform";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 font-head font-semibold rounded-xl " +
  "transition-[filter,background-color,border-color,opacity] select-none whitespace-nowrap " +
  "disabled:opacity-50 disabled:pointer-events-none";

const variants: Record<Variant, string> = {
  // Signal amber: the single, unmistakable primary action.
  primary: "bg-amber text-on-amber hover:brightness-105 active:brightness-95",
  secondary: "bg-transparent text-ink border border-edge-3 hover:bg-surface-2",
  ghost: "bg-transparent text-muted hover:bg-surface-2 hover:text-ink",
  danger: "bg-red text-base hover:brightness-105 active:brightness-95",
  // Teal: the platform / super-admin SIGN-IN accent (login, 2FA verify) - a
  // separate axis from the amber tenant primary (6.6).
  teal: "bg-teal text-base hover:brightness-105 active:brightness-95",
  // Purple: the platform first-time SETUP accent (set password, enroll 2FA,
  // recovery codes) - mirrors the tenant red setup axis.
  platform: "bg-platform text-on-platform hover:brightness-105 active:brightness-95",
};

// Heights/type read the control-sizing tokens (index.css) so every button
// grows in POS mode (>=48px touch targets) with no call-site changes. A
// min-w keeps touch buttons from collapsing below a comfortable tap width.
const sizes: Record<Size, string> = {
  sm: "h-(--ctl-h-sm) px-3.5 text-(length:--ctl-fs)",
  md: "h-(--ctl-h) px-(--ctl-px) text-(length:--ctl-fs)",
  lg: "h-(--ctl-h-lg) px-6 text-(length:--ctl-fs-lg)",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  /**
   * The capability this action needs (§6.3). Without it the button renders
   * DISABLED with an explanation, instead of staying live and failing on the
   * server's 403.
   *
   * Disabled rather than hidden, deliberately, and the two rules differ by what
   * the control is:
   *
   *   * NAVIGATION is hidden — a menu entry to a screen you cannot open is a
   *     dead end, so the sidebar and the route guard remove it entirely
   *     (`auth/routeAccess`).
   *   * An ACTION is disabled — the button is part of how the screen explains
   *     itself. A cashier who can see an invoice should see that issuing it is a
   *     thing that happens, greyed out, and be able to ask an owner for it.
   *     Silently removing it makes the product look like it cannot do the job.
   *
   * An array means any-of, matching `can`. Pass the capability the SERVER
   * enforces on the request this button fires, so the two never disagree.
   */
  requires?: CapabilityNeed;
  /**
   * The PLATFORM capability this action needs (6.6) — the super-admin console's
   * axis, which is a different one entirely.
   *
   * It must be a separate prop rather than more values in `requires`, because the
   * two axes read different sessions: a platform admin has no tenant session at
   * all, so `requires` on a console button would ask the tenant `can`, find no
   * user, fail closed, and disable every button in the console. Passing the wrong
   * prop is therefore a visible bug rather than a silent hole, and the union types
   * keep them from being mixed up.
   *
   * Only valid inside the console's route tree (`/platform`), where
   * PlatformProtectedRoute has put the admin on outlet context.
   */
  requiresPlatform?: PlatformNeed;
}

export function Button(props: ButtonProps) {
  // Each gate lives in its own child component so its hook only runs where that
  // capability is actually declared: Button is the most-used component in the app,
  // including on the pre-session screens (login, password reset) where `useAuth`
  // throws, and in the tenant app where the console's outlet context is absent.
  // Both props are static per call site, so this never switches type mid-life.
  if (props.requires) return <GatedButton {...props} />;
  if (props.requiresPlatform) return <PlatformGatedButton {...props} />;
  return <BaseButton {...props} />;
}

function PlatformGatedButton({ requiresPlatform, ...rest }: ButtonProps) {
  const can = usePlatformCan();
  return can(requiresPlatform!) ? <BaseButton {...rest} /> : <DeniedButton {...rest} />;
}

function GatedButton({ requires, ...rest }: ButtonProps) {
  const { can } = useAuth();
  return can(requires!) ? <BaseButton {...rest} /> : <DeniedButton {...rest} />;
}

// The shared "you may not do this" rendering, so the two capability axes cannot
// drift into explaining themselves differently.
//
// The title sits on a wrapping span, not the button: `disabled:pointer-events-none`
// means a disabled button receives no hover at all, so a title on it would never
// show. The span still does. (A full Tooltip is not an option here — it renders
// its own <button> as the trigger, and nesting buttons is invalid HTML.)
function DeniedButton({ className, ...rest }: ButtonProps) {
  return (
    <span title={NO_PERMISSION_HINT} className={cn("inline-flex", rest.fullWidth && "w-full")}>
      <BaseButton {...rest} className={className} disabled aria-disabled />
    </span>
  );
}

// Deliberately not localised through i18next here: the kit is imported by the
// pre-session screens, which mount before the i18n instance is guaranteed, and a
// missing-key fallback on a permission message is worse than one English string.
// The trilingual requirement applies to the product's own copy (§1); this is the
// same class of string as an aria-label.
const NO_PERMISSION_HINT =
  "You don't have permission for this. Ask an owner or manager to grant it.";

function BaseButton({
  variant = "primary",
  size = "md",
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth,
  className,
  children,
  disabled,
  requires: _requires,
  ...props
}: ButtonProps) {
  return (
    <button
      // Default to type="button" so a Button placed inside a <form> never
      // submits it by accident (a bare <button> defaults to type="submit").
      // Submit buttons opt in explicitly with type="submit", which the spread
      // below preserves by overriding this default.
      type="button"
      className={cn(base, variants[variant], sizes[size], fullWidth && "w-full", className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Spinner size={16} /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}
